import PropTypes from "prop-types";
import { useWatch } from "react-hook-form";

import { FieldGroup } from "@/components/ui/field";
import {
  FormRecordMultiPicker,
  FormRecordPicker,
  SectionCard,
} from "components/BaseForm";
import CONFIG from "Config";
import { useRelationRules } from "routes/revenue_opportunity/form/sections/useRelationRules";
import CampaignService from "services/Campaign";
import CaseService from "services/Case";
import ContactService from "services/Contact";
import EntityService from "services/Entity";

const withSearch = (search, filters) =>
  search ? { ...filters, search_query: search } : filters;

const toDisplay = (id, label) => (id != null ? { id, label } : undefined);

/**
 * Labels for the relation values the form opens with. The pickers only fetch
 * options when opened, so a preselected value needs its name up front.
 */
export const buildRelationDisplays = (record, presetRecords = {}) => {
  if (record) {
    return {
      entity: toDisplay(record.entity_id, record.entity_name),
      campaign: toDisplay(record.campaign_id, record.campaign_name),
      contact: toDisplay(record.contact_id, record.contact?.full_name),
      cases: (record.cases ?? []).map(c => ({
        id: c.id,
        label: c.name ?? c.case_id,
      })),
    };
  }
  const { entity, campaign, contact, caseRecord } = presetRecords;
  return {
    entity: toDisplay(entity?.id, entity?.name),
    campaign: toDisplay(campaign?.id, campaign?.name),
    contact: toDisplay(contact?.id, contact?.full_name),
    cases: caseRecord
      ? [{ id: caseRecord.id, label: caseRecord.name ?? caseRecord.case_id }]
      : [],
  };
};

const RelationsSection = ({ mode, origin, recordCaseIds, displays }) => {
  const [entityId, contactId, campaignId, caseIds] = useWatch({
    name: ["entity_id", "contact_id", "campaign_id", "case_ids"],
  });
  const primaryCaseId = caseIds?.[0] ?? null;
  const { onRelationChange, derivedEntity, readOnly } = useRelationRules({
    mode,
    origin,
    recordCaseIds,
  });

  const fetchEntities = search =>
    EntityService.getEntitiesForFilters(
      withSearch(search, { case_id: primaryCaseId, contact_id: contactId })
    );

  const fetchContacts = search =>
    ContactService.getContactsForFilters(
      withSearch(search, {
        campaign_id: campaignId,
        entity_id: entityId,
        case_id: primaryCaseId,
      })
    );

  const fetchCampaigns = search =>
    CampaignService.getCampaignsForFilters(
      withSearch(search, {
        ...(entityId ? { entity_id: entityId } : { has_entities: true }),
        case_id: primaryCaseId,
        contact_id: contactId,
      })
    );

  const fetchCases = search =>
    CaseService.getForFilters(
      withSearch(
        search,
        entityId ? { entity_id: entityId } : { has_entity: true }
      )
    );

  return (
    <SectionCard title="Relations">
      <FieldGroup className="tw:grid tw:grid-cols-1 tw:gap-5 tw:md:grid-cols-2">
        <FormRecordPicker
          name="entity_id"
          label="Entity"
          required
          placeholder="Pick an Entity"
          fetchOptions={fetchEntities}
          optionsKey={`ro-entity-${primaryCaseId}-${contactId}`}
          display={derivedEntity ?? displays.entity}
          readOnly={readOnly.entity_id}
          onValueChange={value => onRelationChange("entity_id", value)}
        />
        <FormRecordPicker
          name="contact_id"
          label="Contact"
          placeholder="Pick a Contact"
          fetchOptions={fetchContacts}
          optionsKey={`ro-contact-${campaignId}-${entityId}-${primaryCaseId}`}
          display={displays.contact}
          readOnly={readOnly.contact_id}
          onValueChange={value => onRelationChange("contact_id", value)}
        />
        <FormRecordPicker
          name="campaign_id"
          label="Campaign"
          placeholder="Pick a Campaign"
          fetchOptions={fetchCampaigns}
          optionsKey={`ro-campaign-${entityId}-${primaryCaseId}-${contactId}`}
          display={displays.campaign}
          readOnly={readOnly.campaign_id}
          onValueChange={value => onRelationChange("campaign_id", value)}
        />
        {CONFIG.ENABLE_PIRACY && (
          <FormRecordMultiPicker
            name="case_ids"
            label="Cases"
            placeholder="Pick Cases"
            fetchOptions={fetchCases}
            optionsKey={`ro-cases-${entityId}`}
            display={displays.cases}
            readOnly={readOnly.case_ids}
            onValueChange={value => onRelationChange("case_ids", value)}
          />
        )}
      </FieldGroup>
    </SectionCard>
  );
};

const displayShape = PropTypes.shape({
  id: PropTypes.number,
  label: PropTypes.string,
});

RelationsSection.propTypes = {
  mode: PropTypes.string.isRequired,
  origin: PropTypes.string,
  recordCaseIds: PropTypes.arrayOf(PropTypes.number),
  displays: PropTypes.shape({
    entity: displayShape,
    campaign: displayShape,
    contact: displayShape,
    cases: PropTypes.arrayOf(displayShape),
  }).isRequired,
};

export default RelationsSection;
